import { Link } from "react-router-dom";
import { LegalLayout } from "./LegalLayout";

const LINK = "text-primary hover:underline";

/**
 * Data Deletion Instructions page.
 *
 * The public URL Meta's app review asks for. It describes the real flows:
 * the self-service account deletion (immediate, behind an emailed code),
 * disconnecting a social account, and the email route for people who cannot
 * sign in. Meta's "Remove app" flow is automated: Meta calls Spark's
 * data-deletion callback (packages/functions/src/social/meta-callbacks.ts),
 * which deletes the person's connected Facebook, Instagram or Threads
 * accounts and answers with a confirmation code and a status page.
 */
export function DataDeletionPage() {
  return (
    <LegalLayout
      title="Data Deletion Instructions"
      meta={
        <>
          <strong>Last Updated:</strong> 22 October 2026
        </>
      }
    >
      <p>
        You can have your data deleted from Spark by Charisol at any time. This
        page explains how, including for data we received from Facebook,
        Instagram, Threads, LinkedIn or X. For what we collect and why, see our{" "}
        <Link to="/privacy" className={LINK}>
          Privacy Policy
        </Link>
        .
      </p>

      <h2>1. Delete your account (fastest)</h2>
      <ol>
        <li>Sign in to Spark.</li>
        <li>
          Go to <strong>Settings &rarr; Account</strong> and choose{" "}
          <strong>Delete account</strong> in the Danger zone.
        </li>
        <li>
          Read what will be removed and tick the box to confirm you understand
          it cannot be undone.
        </li>
        <li>
          We email a 6-digit code to your account email address. It expires
          after 15 minutes.
        </li>
        <li>
          Enter the code, your password (if your account has one), your
          authenticator code (if you use one), and type your email address to
          confirm.
        </li>
      </ol>
      <p>
        Deletion starts as soon as you confirm; it is not a delayed or
        recoverable request. Everything is normally removed within minutes, and
        you receive an email when it has finished. If a step fails, our system
        keeps retrying until it completes.
      </p>

      <h3>What is deleted</h3>
      <ul>
        <li>Your profile, personas and public profile.</li>
        <li>
          All drafts and their version history, posts, content, campaigns, ideas
          and research.
        </li>
        <li>Uploaded files, generated images and exports.</li>
        <li>
          Your connected social accounts: the stored access tokens and profile
          details for LinkedIn, X, Facebook, Instagram and Threads are deleted
          and, where the platform supports it, we ask the platform to revoke
          them.
        </li>
        <li>Any posts still scheduled to go out.</li>
        <li>Your sign-in accounts.</li>
      </ul>

      <h3>What is kept</h3>
      <ul>
        <li>
          Usage, billing and audit records in anonymized form: counts and dates
          only, with your identity replaced by a random identifier that is not
          stored alongside your user ID. They can no longer be linked to you.
        </li>
        <li>
          Posts already published to LinkedIn, X, Facebook, Instagram or
          Threads. They live on those platforms under your account there, so
          delete them on the platform if you want them gone.
        </li>
      </ul>

      <h2>2. Disconnect a social account only</h2>
      <p>
        If you want Spark to stop using one social account but keep the rest of
        your Spark account:
      </p>
      <ol>
        <li>
          Go to <strong>Settings &rarr; Connections</strong> in Spark.
        </li>
        <li>Choose the account and select Disconnect.</li>
      </ol>
      <p>
        The stored tokens and profile details for that connection are deleted
        immediately, and Spark can no longer read or post to it. Posts you
        already published remain on the platform. You can also remove
        Spark&rsquo;s access from the platform&rsquo;s own settings (see{" "}
        <Link to="/privacy" className={LINK}>
          Privacy Policy, section 3.4
        </Link>
        ).
      </p>

      <h2>3. If you removed Spark in Facebook (&ldquo;Remove app&rdquo;)</h2>
      <p>
        If you remove Spark from your Facebook or Instagram settings (Settings
        &rarr; Business Integrations or Apps and Websites), or from your Threads
        settings, the platform stops allowing Spark to access your account and
        tells Spark so. Spark then switches off the connected account and shows
        &ldquo;Needs reconnecting&rdquo; under Settings &rarr; Connections.
      </p>
      <p>
        If you also ask Facebook, Instagram or Threads to delete your data held
        by Spark, the platform sends Spark a signed deletion request. Spark
        verifies the signature and, straight away, deletes the connected
        Facebook Pages, Instagram accounts and Threads profiles linked to your
        login: the stored access tokens, the account name, handle and picture.
        The platform shows you a confirmation code and a link to a status page
        where you can see that the deletion finished.
      </p>
      <p>
        Removing the app on its own does not delete the rest of your Spark
        account (your drafts, posts and files). To delete those, delete your
        Spark account (section 1), or email us as described below.
      </p>

      <h2>4. If you cannot sign in</h2>
      <p>
        Email{" "}
        <a
          href="mailto:info@charisol.io?subject=Data%20deletion%20request"
          className={LINK}
        >
          info@charisol.io
        </a>{" "}
        with the subject &ldquo;Data deletion request&rdquo;, from the email
        address registered on your Spark account. Tell us whether you want your
        whole account deleted or only the data from a particular platform. We
        may ask you to confirm your identity before deleting anything, so that
        no one else can delete your account. We will confirm by email when it is
        done, and complete it within 30 days, usually much sooner.
      </p>

      <h2>5. Questions</h2>
      <p>
        Contact{" "}
        <a href="mailto:info@charisol.io" className={LINK}>
          info@charisol.io
        </a>
        .
      </p>
    </LegalLayout>
  );
}
